import React, { useCallback } from 'react';

import { type MediaProvider } from '@atlaskit/editor-common/provider-factory';
import { type UploadRejectionData } from '@atlaskit/media-picker';
import { createBuiltInColorMap } from '@atlassian/color-picker';
import {
	getHubDefaultParameters,
	HubErrorLogger,
	logException,
	ProductDependenciesContext,
	type ProductDependenciesProviderResponse,
	useHubContext,
} from '@atlassian/company-hub-common';

interface createMediaProviderProps {
	onUploadRejection?: (data: UploadRejectionData) => boolean;
}

export const CommonHubExtensionsDependenciesProvider = ({
	children,
}: React.PropsWithChildren<unknown>): React.JSX.Element => {
	const { cloudId, cloudUrl, pageMediaConfig, hubMediaConfig, env, branding } = useHubContext();
	const { hoistedProjectKey } = env;

	const defaultParameters = getHubDefaultParameters();

	const brandBackgroundColorMap = React.useMemo(() => {
		const brandBackgroundColors: string[] = [defaultParameters.backgroundColor];
		return createBuiltInColorMap({
			knownColors: brandBackgroundColors,
		});
	}, [defaultParameters]);

	const authProvider = useCallback(
		() =>
			Promise.resolve({
				asapIssuer: pageMediaConfig.asapIssuer,
				token: pageMediaConfig.mediaToken,
				baseUrl: pageMediaConfig.mediaUrl,
			}),
		[pageMediaConfig],
	);

	const createEditorMediaProvider = useCallback(
		async ({ onUploadRejection }: createMediaProviderProps): Promise<MediaProvider> => {
			const mediaProvider: MediaProvider = {
				uploadParams: {
					collection: pageMediaConfig.mediaCollectionName,
					onUploadRejection,
				},
				viewMediaClientConfig: {
					authProvider,
				},
				uploadMediaClientConfig: {
					authProvider,
				},
			};

			return mediaProvider;
		},
		[authProvider, pageMediaConfig],
	);

	const useMediaConfig = useCallback(
		({ imageSrc }: { imageSrc: string }) => {
			return {
				authProvider,
				collection: pageMediaConfig.mediaCollectionName,
				fileId: imageSrc,
			};
		},
		[authProvider, pageMediaConfig],
	);

	const contextValue: ProductDependenciesProviderResponse = {
		smartCardProviderComponent: undefined,
		isTargetAudience: () => false,
		useSegmentationTargetGroups: () => ({}),
		isCompanyHub: true, // need to pass this true to enable company hub specific extensions
		enableLinkInputInMediaPicker: true,
		cloudId,
		cloudUrl,
		hoistedProjectKey,
		helpCenterAri: env.helpCenterAri,
		isAdminHubAIEnabled: false,
		linkPickerConfig: { linkPickerPlugins: [] },
		userId: '',
		customMediaConfig: hubMediaConfig,
		createEditorMediaProvider,
		useMediaConfig,
		branding: {
			...branding,
			defaultParameters,
			hideDefaultColorPalette: !!branding?.colors?.primary,
			brandBackgroundColorMap,
		},
		hideExtensionBorders: true,
		showConfigPreambleSupportLink: false,
		enableTargeting: false,
		enableUnifiedSearchFeatures: false,
		enableSmartCardsFields: false,
		experienceStopComponent: HubErrorLogger,
		submitError: logException,
	};

	return (
		<ProductDependenciesContext.Provider value={contextValue}>
			{children}
		</ProductDependenciesContext.Provider>
	);
};
